/**
 * RedisCacheService - Frontend Redis cache integration for LocalDocumentEngine
 * 
 * Implements L1/L2 hybrid caching strategy based on the Redis implementation
 * documentation with 99%+ cache hit rate potential.
 */

import type { CacheService } from '../core/LocalDocumentEngine';
import type { Operation } from '../types/DocumentTypes';

export interface RedisCacheConfig {
  redisUrl?: string;
  keyPrefix?: string;
  defaultTTL?: number;
  enableL1Cache?: boolean;
  l1CacheSize?: number;
  enableCompression?: boolean;
  compressionThreshold?: number;
  // Backend API configuration
  backendUrl?: string;
  enableBackendCache?: boolean;
}

export interface CacheMetrics {
  l1Hits: number;
  l2Hits: number;
  misses: number;
  totalRequests: number;
  l1HitRate: number;
  l2HitRate: number;
  overallHitRate: number;
  compressionSavings: number;
}

/**
 * L1 (Local) Cache using Map with LRU eviction
 */
class L1Cache {
  private cache = new Map<string, { value: any; timestamp: number; accessCount: number }>();
  private maxSize: number;
  
  constructor(maxSize: number = 1000) {
    this.maxSize = maxSize;
  }
  
  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    // Update access count and timestamp for LRU
    entry.accessCount++;
    entry.timestamp = Date.now();
    
    return entry.value;
  }
  
  set(key: string, value: any): void {
    // Evict if at capacity
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }
    
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      accessCount: 1
    });
  }
  
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;
    let lowestAccess = Infinity;
    
    for (const [key, entry] of this.cache.entries()) {
      // Prefer evicting items with low access count and old timestamp
      const score = entry.accessCount * 1000 + (Date.now() - entry.timestamp);
      if (score < (lowestAccess * 1000 + oldestTime)) {
        oldestKey = key;
        oldestTime = Date.now() - entry.timestamp;
        lowestAccess = entry.accessCount;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }
  
  clear(): void {
    this.cache.clear();
  }
  
  size(): number {
    return this.cache.size;
  }
}

export class RedisCacheService implements CacheService {
  private config: Required<RedisCacheConfig>;
  private l1Cache: L1Cache;
  private metrics: CacheMetrics = {
    l1Hits: 0,
    l2Hits: 0,
    misses: 0,
    totalRequests: 0,
    l1HitRate: 0,
    l2HitRate: 0,
    overallHitRate: 0,
    compressionSavings: 0
  };
  
  // Mock Redis client for frontend (in production, this would connect to Redis via API)
  private redisClient: any = null;
  
  constructor(config: RedisCacheConfig = {}) {
    this.config = {
      redisUrl: config.redisUrl || 'redis://localhost:6380',
      keyPrefix: config.keyPrefix || 'edgelex:harfbuzz:',
      defaultTTL: config.defaultTTL || 3600,
      enableL1Cache: config.enableL1Cache ?? true,
      l1CacheSize: config.l1CacheSize || 1000,
      enableCompression: config.enableCompression ?? true,
      compressionThreshold: config.compressionThreshold || 1024,
      // Backend API configuration (ENABLED BY DEFAULT)
      backendUrl: config.backendUrl || window.location.origin,
      enableBackendCache: config.enableBackendCache ?? true
    };
    
    this.l1Cache = new L1Cache(this.config.l1CacheSize);
    
    console.log('[RedisCacheService] Initialized with config:', {
      keyPrefix: this.config.keyPrefix,
      l1CacheSize: this.config.l1CacheSize,
      enableCompression: this.config.enableCompression
    });
    
    // Initialize Redis connection (mock for frontend)
    this.initializeRedisConnection();
  }

  /**
   * Get value from L1/L2 hybrid cache
   */
  async get(key: string): Promise<any> {
    this.metrics.totalRequests++;
    const fullKey = this.config.keyPrefix + key;
    
    try {
      // L1 Cache check (fastest - 1-2ms)
      if (this.config.enableL1Cache) {
        const l1Result = this.l1Cache.get(fullKey);
        if (l1Result !== null) {
          this.metrics.l1Hits++;
          this.updateMetrics();
          console.log(`[RedisCacheService] ✅ L1 cache hit: ${key}`);
          return l1Result;
        }
      }
      
      // L2 Cache check (Redis - 3-5ms)
      const l2Result = await this.getFromRedis(fullKey);
      if (l2Result !== null) {
        this.metrics.l2Hits++;
        
        // Populate L1 cache
        if (this.config.enableL1Cache) {
          this.l1Cache.set(fullKey, l2Result);
        }
        
        this.updateMetrics();
        console.log(`[RedisCacheService] ✅ L2 cache hit: ${key}`);
        return l2Result;
      }
      
      // Cache miss
      this.metrics.misses++;
      this.updateMetrics();
      console.log(`[RedisCacheService] ❌ Cache miss: ${key}`);
      return null;
      
    } catch (error) {
      console.warn(`[RedisCacheService] Cache get failed for ${key}:`, error);
      this.metrics.misses++;
      this.updateMetrics();
      return null;
    }
  }

  /**
   * Set value in L1/L2 hybrid cache
   */
  async set(key: string, value: any, ttl?: number): Promise<void> {
    const fullKey = this.config.keyPrefix + key;
    const cacheTTL = ttl || this.config.defaultTTL;
    
    try {
      // Store in L1 cache (synchronous)
      if (this.config.enableL1Cache) {
        this.l1Cache.set(fullKey, value);
      }
      
      // Store in L2 cache (asynchronous, non-blocking)
      this.setInRedis(fullKey, value, cacheTTL).catch(error => {
        console.warn(`[RedisCacheService] Redis cache write failed for ${key}:`, error);
      });
      
      console.log(`[RedisCacheService] ✅ Cache set: ${key} (TTL: ${cacheTTL}s)`);
      
    } catch (error) {
      console.warn(`[RedisCacheService] Cache set failed for ${key}:`, error);
    }
  }

  /**
   * Invalidate cache entries by pattern
   */
  async invalidate(pattern: string): Promise<void> {
    const fullPattern = this.config.keyPrefix + pattern;
    
    try {
      // Clear L1 cache entries matching pattern
      if (this.config.enableL1Cache) {
        // Simple pattern matching for L1 cache
        const keysToDelete: string[] = [];
        for (const key of Array.from(this.l1Cache['cache'].keys())) {
          if (this.matchesPattern(key, fullPattern)) {
            keysToDelete.push(key);
          }
        }
        
        keysToDelete.forEach(key => {
          this.l1Cache['cache'].delete(key);
        });
        
        console.log(`[RedisCacheService] ✅ L1 invalidated ${keysToDelete.length} keys for pattern: ${pattern}`);
      }
      
      // Invalidate L2 cache (Redis)
      await this.invalidateInRedis(fullPattern);
      
    } catch (error) {
      console.warn(`[RedisCacheService] Cache invalidation failed for pattern ${pattern}:`, error);
    }
  }

  /**
   * Generate cache key for operation
   */
  generateKey(operation: Operation): string {
    // Create deterministic key from operation content
    const content = operation.type === 'insert' 
      ? (operation as any).content 
      : operation.type === 'delete' 
        ? (operation as any).deletedContent 
        : '';
    
    const keyData = {
      type: operation.type,
      position: operation.position,
      content: content,
      version: operation.version,
      attorney: operation.attorney?.id || 'unknown'
    };
    
    // Simple hash function for key generation
    const keyString = JSON.stringify(keyData);
    const hash = this.simpleHash(keyString);
    
    return `op:${operation.type}:${hash}`;
  }

  /**
   * Get cache metrics for monitoring
   */
  getMetrics(): CacheMetrics {
    return { ...this.metrics };
  }

  /**
   * Clear all cache data
   */
  async clear(): Promise<void> {
    try {
      // Clear L1 cache
      if (this.config.enableL1Cache) {
        this.l1Cache.clear();
      }
      
      // Clear L2 cache
      await this.clearRedis();
      
      // Reset metrics
      this.metrics = {
        l1Hits: 0,
        l2Hits: 0,
        misses: 0,
        totalRequests: 0,
        l1HitRate: 0,
        l2HitRate: 0,
        overallHitRate: 0,
        compressionSavings: 0
      };
      
      console.log('[RedisCacheService] ✅ Cache cleared');
      
    } catch (error) {
      console.warn('[RedisCacheService] Cache clear failed:', error);
    }
  }

  /**
   * Initialize Redis connection via backend API
   */
  private async initializeRedisConnection(): Promise<void> {
    try {
      if (this.config.enableBackendCache) {
        // Test backend cache connectivity
        const response = await fetch(`${this.config.backendUrl}/api/v1/cache/health`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const health = await response.json();
          this.redisClient = {
            connected: true,
            url: this.config.backendUrl,
            backend: true,
            latency: health.latency
          };
          console.log('[RedisCacheService] ✅ Backend Redis connection initialized:', health);
        } else {
          throw new Error(`Backend cache health check failed: ${response.status}`);
        }
      } else {
        // Fallback to localStorage mock
        this.redisClient = {
          connected: true,
          url: this.config.redisUrl,
          backend: false
        };
        console.log('[RedisCacheService] ✅ localStorage fallback initialized');
      }
      
    } catch (error) {
      console.warn('[RedisCacheService] Backend cache failed, falling back to localStorage:', error);
      this.redisClient = {
        connected: true,
        url: 'localStorage',
        backend: false,
        fallback: true
      };
    }
  }

  /**
   * Get authentication token for backend requests
   */
  private getAuthToken(): string {
    return localStorage.getItem('webelieve_access_token') || '';
  }

  /**
   * Get value from Redis via backend API or localStorage fallback
   */
  private async getFromRedis(key: string): Promise<any> {
    if (!this.redisClient) return null;
    
    try {
      if (this.redisClient.backend && !this.redisClient.fallback) {
        // Use backend Redis API
        const response = await fetch(`${this.config.backendUrl}/api/v1/cache/${encodeURIComponent(key)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          return result.success ? result.value : null;
        } else if (response.status === 404) {
          return null; // Cache miss
        } else {
          throw new Error(`Backend cache GET failed: ${response.status}`);
        }
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem(`redis:${key}`);
        if (!stored) return null;
        
        const data = JSON.parse(stored);
        
        // Check TTL
        if (data.expires && Date.now() > data.expires) {
          localStorage.removeItem(`redis:${key}`);
          return null;
        }
        
        // Decompress if needed
        const value = this.config.enableCompression && data.compressed
          ? this.decompress(data.value)
          : data.value;
        
        return value;
      }
      
    } catch (error) {
      console.warn(`[RedisCacheService] Redis get failed for ${key}, falling back to localStorage:`, error);
      
      // Fallback to localStorage on any error
      try {
        const stored = localStorage.getItem(`redis:${key}`);
        if (!stored) return null;
        
        const data = JSON.parse(stored);
        if (data.expires && Date.now() > data.expires) {
          localStorage.removeItem(`redis:${key}`);
          return null;
        }
        
        return this.config.enableCompression && data.compressed
          ? this.decompress(data.value)
          : data.value;
      } catch (fallbackError) {
        console.warn(`[RedisCacheService] localStorage fallback also failed:`, fallbackError);
        return null;
      }
    }
  }

  /**
   * Set value in Redis via backend API or localStorage fallback
   */
  private async setInRedis(key: string, value: any, ttl: number): Promise<void> {
    if (!this.redisClient) return;
    
    try {
      if (this.redisClient.backend && !this.redisClient.fallback) {
        // Use backend Redis API
        const response = await fetch(`${this.config.backendUrl}/api/v1/cache/${encodeURIComponent(key)}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            value: value,
            ttl: ttl
          })
        });
        
        if (!response.ok) {
          throw new Error(`Backend cache SET failed: ${response.status}`);
        }
        
        const result = await response.json();
        if (result.compressed) {
          const originalSize = JSON.stringify(value).length;
          const compressedSize = result.size || originalSize;
          this.metrics.compressionSavings += Math.max(0, originalSize - compressedSize);
        }
      } else {
        // Fallback to localStorage
        const serialized = JSON.stringify(value);
        const shouldCompress = this.config.enableCompression && 
                             serialized.length > this.config.compressionThreshold;
        
        const data = {
          value: shouldCompress ? this.compress(serialized) : value,
          compressed: shouldCompress,
          expires: Date.now() + (ttl * 1000),
          created: Date.now()
        };
        
        // Track compression savings
        if (shouldCompress) {
          const originalSize = serialized.length;
          const compressedSize = JSON.stringify(data.value).length;
          this.metrics.compressionSavings += originalSize - compressedSize;
        }
        
        localStorage.setItem(`redis:${key}`, JSON.stringify(data));
      }
      
    } catch (error) {
      console.warn(`[RedisCacheService] Redis set failed for ${key}, falling back to localStorage:`, error);
      
      // Fallback to localStorage on any error
      try {
        const serialized = JSON.stringify(value);
        const shouldCompress = this.config.enableCompression && 
                             serialized.length > this.config.compressionThreshold;
        
        const data = {
          value: shouldCompress ? this.compress(serialized) : value,
          compressed: shouldCompress,
          expires: Date.now() + (ttl * 1000),
          created: Date.now()
        };
        
        localStorage.setItem(`redis:${key}`, JSON.stringify(data));
      } catch (fallbackError) {
        console.warn(`[RedisCacheService] localStorage fallback also failed:`, fallbackError);
      }
    }
  }

  /**
   * Invalidate Redis keys by pattern via backend API or localStorage fallback
   */
  private async invalidateInRedis(pattern: string): Promise<void> {
    if (!this.redisClient) return;
    
    try {
      if (this.redisClient.backend && !this.redisClient.fallback) {
        // Use backend Redis API
        const response = await fetch(`${this.config.backendUrl}/api/v1/cache/invalidate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pattern: pattern
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log(`[RedisCacheService] ✅ Backend invalidated ${result.deletedCount} keys for pattern: ${pattern}`);
        } else {
          throw new Error(`Backend cache INVALIDATE failed: ${response.status}`);
        }
      } else {
        // Fallback to localStorage
        const keysToDelete: string[] = [];
        
        // Find matching keys in localStorage
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('redis:') && this.matchesPattern(key.slice(6), pattern)) {
            keysToDelete.push(key);
          }
        }
        
        // Delete matching keys
        keysToDelete.forEach(key => localStorage.removeItem(key));
        
        console.log(`[RedisCacheService] ✅ localStorage invalidated ${keysToDelete.length} keys`);
      }
      
    } catch (error) {
      console.warn(`[RedisCacheService] Redis invalidation failed for pattern ${pattern}, falling back to localStorage:`, error);
      
      // Fallback to localStorage on any error
      try {
        const keysToDelete: string[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('redis:') && this.matchesPattern(key.slice(6), pattern)) {
            keysToDelete.push(key);
          }
        }
        
        keysToDelete.forEach(key => localStorage.removeItem(key));
      } catch (fallbackError) {
        console.warn('[RedisCacheService] localStorage fallback invalidation also failed:', fallbackError);
      }
    }
  }

  /**
   * Clear all Redis data via backend API or localStorage fallback
   */
  private async clearRedis(): Promise<void> {
    if (!this.redisClient) return;
    
    try {
      if (this.redisClient.backend && !this.redisClient.fallback) {
        // Use backend Redis API - clear all cache entries for this realm
        await this.invalidateInRedis('*'); // Clear all keys with realm prefix
      } else {
        // Fallback to localStorage
        const keysToDelete: string[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('redis:')) {
            keysToDelete.push(key);
          }
        }
        
        keysToDelete.forEach(key => localStorage.removeItem(key));
      }
      
    } catch (error) {
      console.warn('[RedisCacheService] Redis clear failed:', error);
    }
  }

  /**
   * Update cache metrics
   */
  private updateMetrics(): void {
    const total = this.metrics.totalRequests;
    if (total > 0) {
      this.metrics.l1HitRate = (this.metrics.l1Hits / total) * 100;
      this.metrics.l2HitRate = (this.metrics.l2Hits / total) * 100;
      this.metrics.overallHitRate = ((this.metrics.l1Hits + this.metrics.l2Hits) / total) * 100;
    }
  }

  /**
   * Simple pattern matching for cache keys
   */
  private matchesPattern(key: string, pattern: string): boolean {
    // Convert glob pattern to regex
    const regexPattern = pattern
      .replace(/\*/g, '.*')
      .replace(/\?/g, '.');
    
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(key);
  }

  /**
   * Simple hash function for key generation
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Mock compression (in production, use actual compression library)
   */
  private compress(data: string): string {
    // Simple mock compression - in production use gzip/brotli
    return btoa(data);
  }

  /**
   * Mock decompression
   */
  private decompress(data: string): any {
    try {
      return JSON.parse(atob(data));
    } catch {
      return data;
    }
  }
}

// Export singleton factory
export const createRedisCacheService = (config?: RedisCacheConfig): RedisCacheService => {
  return new RedisCacheService(config);
};

